import React, { useMemo, useState } from "react";
import { Table } from "antd";
import CcButton from "../../CoilConfigurator/components/CcButton";
import OpStatusPill from "../components/OpStatusPill";
import type { ConfigurationResult } from "../models/types";

interface ConfigurationsDashboardProps {
  rows: ConfigurationResult[];
  loading: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
}

const ConfigurationsDashboard = ({
  rows,
  loading,
  onCreate,
  onOpen,
}: ConfigurationsDashboardProps) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.project.projectNumber.toLowerCase().includes(q) ||
        row.project.projectName.toLowerCase().includes(q) ||
        row.configurationId.toLowerCase().includes(q),
    );
  }, [query, rows]);

  return (
    <div className="op-dashboard">
      <div className="op-toolbar">
        <input
          className="op-search"
          type="search"
          placeholder="Search by project or configuration ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search configurations"
        />
        <CcButton variant="primary" onClick={onCreate}>
          + New Configuration
        </CcButton>
      </div>

      <h2 className="op-section-title">Recent Configurations</h2>
      <Table
        className="op-table"
        loading={loading}
        rowKey="configurationId"
        dataSource={filtered}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "No configurations yet. Create one to get started." }}
        columns={[
          { title: "Project", dataIndex: ["project", "projectNumber"], key: "project" },
          {
            title: "Equipment",
            key: "equipment",
            render: (_: unknown, row: ConfigurationResult) => {
              const parts = [
                row.blastFrame ? "BF" : null,
                row.inductionCoil ? "IC" : null,
              ].filter(Boolean);
              return parts.length ? parts.join(" + ") : "—";
            },
          },
          {
            title: "Status",
            key: "status",
            render: (_: unknown, row: ConfigurationResult) => <OpStatusPill status={row.status} />,
          },
          { title: "Revision", dataIndex: "revision", key: "revision" },
          {
            title: "Actions",
            key: "actions",
            render: (_: unknown, row: ConfigurationResult) => (
              <CcButton variant="link" onClick={() => onOpen(row.configurationId)}>
                Open
              </CcButton>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ConfigurationsDashboard;
